import { GroupData } from "../types/GroupData";
import { ResultPagesData } from "../hooks/useResultPages";
import { getResultKey, getNumericResult, getSortedGroups } from "./scoreUtils";

export interface RoundAverages {
  mus: number;
  per: number;
  sng: number;
  total: number;
  count: number; // 集計対象の演奏数
}

export interface YearlyAverages {
  year: number;
  semiFinal: RoundAverages;
  final: RoundAverages;
  chorus: RoundAverages;
}

const EMPTY_ROUND: RoundAverages = {
  mus: 0,
  per: 0,
  sng: 0,
  total: 0,
  count: 0,
};

const average = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sum = values.reduce((acc, v) => acc + v, 0);
  return Math.round((sum / values.length) * 10) / 10;
};

// 対象グループ群・対象曲番号から、MUS/PER/SNG/TOTALの平均を算出する
// （アプリ内のgetAverageBySongsと同じ考え方：該当する曲のスコアをすべて平均する。
//   ペナルティチェックは表示上の強調用のみで、平均には反映されない仕様に合わせている）
const calcRoundAverages = (
  targetGroups: GroupData[],
  songNums: number[],
): RoundAverages => {
  if (targetGroups.length === 0) return { ...EMPTY_ROUND };

  const musValues: number[] = [];
  const perValues: number[] = [];
  const sngValues: number[] = [];
  const totalValues: number[] = [];

  targetGroups.forEach((group) => {
    (["Mus", "Per", "Sng"] as const).forEach((type) => {
      songNums.forEach((num) => {
        const key = getResultKey(type, num);
        const value = getNumericResult(group[key as keyof GroupData]);

        if (type === "Mus") musValues.push(value);
        if (type === "Per") perValues.push(value);
        if (type === "Sng") sngValues.push(value);
        totalValues.push(value);
      });
    });
  });

  return {
    mus: average(musValues),
    per: average(perValues),
    sng: average(sngValues),
    total: average(totalValues),
    count: targetGroups.length,
  };
};

// Finalに進出したグループのみ抽出
// （ResultLists.tsxの絞り込みと同じロジック：得点順に並べ替えてfinalistCount件だけ残す）
const getFinalistGroups = (
  quartetGroupsOfYear: GroupData[],
  finalistCount: number,
): GroupData[] => {
  if (finalistCount <= 0) return [];

  const scoreSorted = getSortedGroups(quartetGroupsOfYear, {}, false, [1, 2]);

  return scoreSorted.slice(0, finalistCount);
};

export const computeYearlyAverages = (
  groups: GroupData[],
  resultPages: ResultPagesData[],
  years: number[],
): YearlyAverages[] => {
  const activeGroups = groups.filter((group) => group.isDeleted !== true);

  return years.map((year) => {
    const groupsOfYear = activeGroups.filter(
      (group) => group.createdAt?.toDate?.().getFullYear?.() === year,
    );

    const quartetGroups = groupsOfYear.filter(
      (group) => group.entryClass === "QuartetContest",
    );
    const chorusGroups = groupsOfYear.filter(
      (group) => group.entryClass === "ChorusContest",
    );

    const finalistCount =
      resultPages.find((page) => Number(page.year) === year)
        ?.finalistCount ?? 0;

    const finalistGroups = getFinalistGroups(quartetGroups, finalistCount);

    return {
      year,
      semiFinal: calcRoundAverages(quartetGroups, [1, 2]),
      final: calcRoundAverages(finalistGroups, [3, 4]),
      chorus: calcRoundAverages(chorusGroups, [1, 2]),
    };
  });
};
